import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getEventById, getEventOccurrences } from '../services/eventService';
import { useLocale } from '../i18n/LocaleContext';
import AppHeader from '../components/AppHeader';
import { ErrorState } from '../components/States';
import { ArrowLeftIcon, PinIcon, ShareIcon, TicketIcon } from '../components/Icons';
import FavouriteButton from '../components/FavouriteButton';
import { getCategoryForType } from '../utils/eventCategories';
import { getEventImageUrl, getFallbackImageUrl } from '../utils/eventImage';
import { safeUrl } from '../utils/safeUrl';
import { useDocumentTitle } from '../utils/useDocumentTitle';
import {
  formatDistance,
  formatFullDate,
  formatPrice,
  getTicketStatus
} from '../utils/format';

export default function EventDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { locale, t } = useLocale();

  const [event, setEvent] = useState(null);
  const [occurrences, setOccurrences] = useState([]);
  const [status, setStatus] = useState('loading');
  const [imageSrc, setImageSrc] = useState(null);
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const data = await getEventById(id);
      setEvent(data);
      setImageSrc(getEventImageUrl(data));
      setStatus('ready');
      setOccurrences(await getEventOccurrences(id));
    } catch (error) {
      console.error('Error loading event:', error);
      setStatus(error.response?.status === 404 ? 'notfound' : 'error');
    }
  }, [id]);

  useEffect(() => {
    load();
    window.scrollTo(0, 0);
  }, [load]);

  useEffect(() => {
    if (!toast) {
      return undefined;
    }
    const timer = setTimeout(() => setToast(''), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  useDocumentTitle(event?.title ? `${event.title} · ${t('app.name')}` : null);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: event.title, url });
        return;
      } catch {
        // User dismissed the share sheet - fall through to copying.
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setToast(t('detail.copied'));
    } catch {
      setToast(url);
    }
  };

  if (status === 'loading') {
    return (
      <div className="app">
        <AppHeader />
        <div className="detail-hero skeleton" />
        <main className="page detail">
          <div className="skeleton" style={{ width: '70%', height: 38, marginBottom: 24 }} />
          <div className="skeleton" style={{ width: '100%', height: 200 }} />
        </main>
      </div>
    );
  }

  if (status !== 'ready') {
    return (
      <div className="app">
        <AppHeader />
        <main className="page">
          <ErrorState onRetry={status === 'error' ? load : undefined} />
        </main>
      </div>
    );
  }

  const category = getCategoryForType(event.event_type);
  const ticketStatus = getTicketStatus(event.tickets_available);
  const isFree = event.lowest_price === 0;
  // React renders a javascript: href with only a warning, so the scheme is
  // filtered here rather than relied on downstream.
  const ticketUrl = safeUrl(event.ticket_url);
  const distance = formatDistance(event.distanceKm, t);
  const mapsQuery = encodeURIComponent(
    event.address || `${event.venue}, ${event.city}`
  );
  const mapsUrl = event.latitude && event.longitude
    ? `https://www.google.com/maps/search/?api=1&query=${event.latitude},${event.longitude}`
    : `https://www.google.com/maps/search/?api=1&query=${mapsQuery}`;

  return (
    <div className="app">
      <AppHeader />

      <div className="detail-hero">
        <button type="button" className="detail-back" onClick={() => navigate(-1)}>
          <ArrowLeftIcon size={16} />
          {t('detail.back')}
        </button>
        <img
          className="detail-hero__image"
          src={imageSrc}
          alt=""
          onError={() => setImageSrc(getFallbackImageUrl(event.event_type))}
        />
      </div>

      <main className="page detail">
        <div className="detail__badges">
          <span className="badge badge--neutral">
            {category.icon} {t(`cat.${category.id}`)}
          </span>
          {isFree && <span className="badge badge--free">{t('price.free')}</span>}
          {ticketStatus === 'limited' && (
            <span className="badge badge--limited">{t('tickets.limited')}</span>
          )}
          {ticketStatus === 'soldout' && (
            <span className="badge badge--soldout">{t('tickets.soldout')}</span>
          )}
        </div>

        <h1 className="detail__title">{event.title}</h1>

        <div className="detail__layout">
          <div>
            <div className="facts">
              <div className="fact">
                <span className="fact__label">{t('detail.when')}</span>
                <span className="fact__value">{formatFullDate(event.date, locale)}</span>
              </div>

              <div className="fact">
                <span className="fact__label">{t('detail.where')}</span>
                <span className="fact__value">
                  {event.venue}
                  <br />
                  <a
                    className="fact__link"
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {event.address || `${event.city}, ${event.country}`}
                  </a>
                  {distance && <span className="card__distance"> · {distance}</span>}
                </span>
              </div>

              {event.music_genre && (
                <div className="fact">
                  <span className="fact__label">{t('detail.genre')}</span>
                  <span className="fact__value">{event.music_genre}</span>
                </div>
              )}

              {event.special_features && (
                <div className="fact">
                  <span className="fact__label">{t('detail.features')}</span>
                  <span className="fact__value">{event.special_features}</span>
                </div>
              )}
            </div>

            {event.description && (
              <section>
                <h2 className="section-title">{t('detail.about')}</h2>
                <p className="prose">{event.description}</p>
              </section>
            )}

            {occurrences.length > 0 && (
              <section className="occurrences">
                <h2 className="section-title">{t('detail.otherDates')}</h2>
                {occurrences.map((occurrence) => (
                  <button
                    key={occurrence._id}
                    type="button"
                    className="occurrence"
                    onClick={() => navigate(`/event/${occurrence._id}`)}
                  >
                    <span>{formatFullDate(occurrence.date, locale)}</span>
                    <span className="occurrence__price">
                      {formatPrice(occurrence.lowest_price, locale, t)}
                    </span>
                  </button>
                ))}
              </section>
            )}
          </div>

          <aside className="ticket-panel">
            <div>
              <div
                className={`ticket-panel__price${isFree ? ' ticket-panel__price--free' : ''}`}
              >
                {formatPrice(event.lowest_price, locale, t)}
              </div>
              {ticketStatus && (
                <div className="ticket-panel__note">{t(`tickets.${ticketStatus}`)}</div>
              )}
            </div>

            {ticketUrl ? (
              <a
                className="btn btn--accent btn--block"
                href={ticketUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <TicketIcon size={17} />
                {t('detail.tickets')}
              </a>
            ) : (
              <span className="ticket-panel__note">{t('detail.noTicketUrl')}</span>
            )}

            <a
              className="btn btn--block"
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <PinIcon size={16} />
              {t('detail.map')}
            </a>

            <FavouriteButton event={event} variant="inline" size={16} />

            <button type="button" className="btn btn--ghost btn--block" onClick={share}>
              <ShareIcon size={16} />
              {t('detail.share')}
            </button>
          </aside>
        </div>
      </main>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
